"use client";

import { useCallback, useEffect, useRef, useState } from "react";

import {
    CarouselApi,
    CarouselContentVisible,
    CarouselItem,
} from "~/components/ui/carousel";

import Container from "~/componentsDS/Layout/Container";
import Rating from "~/componentsND/Rating";
import Paragraph from "~/componentsDS/Typography/Paragraph";
import Overline from "~/componentsDS/Typography/Overline";
import Heading from "~/componentsDS/Typography/Heading";
import Carousel from "~/componentsDS/Carousel";
import Reveal from "~/componentsDS/Reveal";
import VideoReview from "~/app/(main)/reviews/components/VideoReview";

import { videoReviews } from "~/app/(main)/reviews/data/video-reviews";
import { QuizCTA as QuizLink } from "~/components/features/quiz";
import { QUIZ_ROUTES } from "~/constants/quizRoutes";
import TextWithRegisteredMark from "~/componentsDS/TextWithRegisteredMark";
import { cn } from "~/lib/utils";
import { GTM_EVENT_NAMES } from "~/constants/gtmEvents";
import { useTrackEvent } from "~/hooks/useTrackEvent";

type VideoInteraction = "play video" | "pause video" | "mute" | "unmute";

export default function VideoReviewsSection() {
    const sendEvent = useTrackEvent();
    const sectionRef = useRef<HTMLElement>(null);
    const [emblaApi, setApi] = useState<CarouselApi>();
    const [activeIndex, setActiveIndex] = useState(0);
    const [isMuted, setMuted] = useState(true);
    const [isNearViewport, setIsNearViewport] = useState(false);

    useEffect(() => {
        const section = sectionRef.current;
        if (!section) return;

        const observer = new IntersectionObserver(
            ([entry]) => {
                if (!entry?.isIntersecting) return;
                setIsNearViewport(true);
                observer.disconnect();
            },
            { rootMargin: "200px 0px" }
        );

        observer.observe(section);

        return () => observer.disconnect();
    }, []);

    const trackVideoInteraction = useCallback((
        action: VideoInteraction,
        video: HTMLVideoElement,
        name: string,
        mp4: string
    ) => {
        const willBeMuted = action === "mute"
            ? true
            : action === "unmute"
                ? false
                : video.muted;

        sendEvent({
            event: "user_interaction",
            value: {
                event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                widget: "real_testimonials",
                meta_data: {
                    click_text: action,
                    product_name: "oestra",
                    vsl_id: "review_vsl",
                    video_name: mp4,
                    vsl_duration: video.duration,
                    vsl_current_time: video.currentTime,
                    vsl_completed: video.ended,
                    vsl_sound: willBeMuted ? "false" : "true",
                    video_person_name: name,
                },
            },
        });
    }, [sendEvent]);

    useEffect(() => {
        if (!emblaApi) return;

        const onSelect = () => {
            const idx = emblaApi.selectedScrollSnap();
            const review = videoReviews[idx];
            const video = sectionRef.current?.querySelectorAll("video")[idx];

            setActiveIndex(idx);

            if (review && video) {
                trackVideoInteraction(
                    "play video",
                    video,
                    review.name,
                    review.mp4
                );
            }
        };

        emblaApi.on("select", onSelect);

        return () => {
            emblaApi.off("select", onSelect);
        };
    }, [emblaApi, trackVideoInteraction]);

    return (
        <section ref={sectionRef} className="pb-16 lg:pb-24 text-pureWhite overflow-hidden">
            <Container>
                <Reveal>
                    <Heading as="h2" size="sm" sizes={{ lg: "md" }} className="text-center mb-4">
                        Real people, <br className="md:hidden" /> real testimonials
                    </Heading>
                </Reveal>

                <Paragraph size="lg" className="text-center mx-auto mb-4">
                    We’ve helped thousands of women find their Inner Balance — <br className="max-md:hidden" />
                    starting with our widely-loved hormone enrichment cream, Oestra<sup>®</sup>.
                </Paragraph>

                <div className="flex items-center justify-center gap-3">
                    <Rating rating={5} ratingColor="text-pureWhite" className="translate-y-[-2px]" />
                    <Overline size="lg" weight="500" className="leading-none opacity-80">4.9/5</Overline>
                    <Overline size="lg" weight="500" className="leading-none opacity-80">5,695 Reviews</Overline>
                </div>
            </Container>

            <Carousel
                setApi={setApi}
                arrows={false}
                dots={false}
                desktopWheelGestures
                className="mt-8 lg:mt-10"
            >
                <CarouselContentVisible className="items-center min-h-[500px]">
                    {videoReviews.map((review, idx) => (
                        <CarouselItem key={idx} className="flex-shrink-0 basis-[316px]" onClick={() => emblaApi?.scrollTo(idx)}>
                            {isNearViewport ? (
                                <VideoReview
                                    {...review}
                                    poster={review.poster}
                                    idx={idx}
                                    activeIndex={activeIndex}
                                    isMuted={isMuted}
                                    setMuted={setMuted}
                                    preload="none"
                                    onUserInteraction={(action, video) => {
                                        if (action === "play video" && idx !== activeIndex) {
                                            return;
                                        }

                                        trackVideoInteraction(action, video, review.name, review.mp4);
                                    }}
                                />
                            ) : (
                                <div
                                    aria-hidden="true"
                                    className={cn(
                                        "w-full rounded-2xl bg-trueBlack/10",
                                        idx === activeIndex ? "aspect-[15/25]" : "aspect-[15/19]"
                                    )}
                                />
                            )}
                        </CarouselItem>
                    ))}
                </CarouselContentVisible>
            </Carousel>

            <Container>
                <div className="flex justify-center mt-6 lg:mt-8">
                    <QuizLink
                        variant="white"
                        href={QUIZ_ROUTES.HORMONE_IMBALANCE}
                        size="md"
                        eventData={{
                            event_name: GTM_EVENT_NAMES.HP_INTERACTION,
                            widget: "real_testimonials",
                            meta_data: {
                                click_text: "Take the Oestra® assessment",
                                product_name: "oestra",
                            },
                        }}
                        className="max-md:w-full"
                    >
                        <TextWithRegisteredMark text="Take the OESTRA® assessment" />
                    </QuizLink>
                </div>
            </Container>
        </section>
    );
}
